import Avatar, { type AvatarProps } from '~/components/new/Avatar';
import { classNames } from '~/utils/styles';
import Tooltip from './Tooltip';

type AvatarListItem<T> = Pick<AvatarProps, 'color' | 'variant' | 'icon' | 'text' | 'image'> &
    Record<string, T>;

type AvatarListProps<T> = {
    items: AvatarListItem<T>[];
    limit?: number;
    onClick?: (item?: AvatarListItem<T>) => void;
    size?: AvatarProps['size'];
    className?: string;
};

export default function AvatarList<T>({
    items,
    limit = 3,
    onClick,
    size = 'large',
    className,
}: AvatarListProps<T>) {
    const displayedItems = [...items].slice(0, limit);
    const overflow = items.length - limit;
    return (
        <ul
            className={classNames({
                [`${className}`]: Boolean(className),
                'avatar-list': true,
                'avatar-list-small': ['xsmall', 'small', 'medium'].includes(size),
            })}>
            {displayedItems.map((item, idx) => {
                const avatarProps = {
                    size,
                    variant: item.variant ?? 'base',
                    color: item.color,
                    className: classNames({
                        'avatar-list-item': true,
                        'cursor-pointer': Boolean(onClick),
                    }),
                    onClick: () => onClick?.(item),
                };
                const typology = item.image ? 'image' : item.text ? 'text' : 'icon';
                return (
                    <Tooltip
                        key={`avatar_list_item_${item.text || item.image || item.icon}_${idx}`}
                        title={item.text}>
                        <li>
                            {/* individual <Avatar /> rendering or TS complains */}
                            {typology === 'icon' && (
                                <Avatar
                                    {...avatarProps}
                                    icon={item.icon!}
                                />
                            )}
                            {typology === 'text' && (
                                <Avatar
                                    {...avatarProps}
                                    text={item.text![0]}
                                />
                            )}
                            {typology === 'image' && (
                                <Avatar
                                    {...avatarProps}
                                    image={item.image!}
                                />
                            )}
                        </li>
                    </Tooltip>
                );
            })}
            {overflow > 0 && (
                <li className='avatar-list-more'>
                    <Avatar
                        text={`+${overflow}`}
                        color='grey'
                        size={size}
                        variant='light'
                        onClick={() => onClick?.()}
                    />
                </li>
            )}
        </ul>
    );
}

AvatarList.Skeleton = function AvatarListSkeleton<T>({
    items,
    className,
    ...otherProps
}: { items: number } & Omit<AvatarListProps<T>, 'items' | 'limit'>) {
    return (
        <AvatarList
            {...otherProps}
            className={`avatar-list-skeleton ${className || ''}`}
            limit={items}
            items={Array(items).fill({ text: 'Placeholder' }, 0, 5)}
        />
    );
};
